// src/pages/Hr/CalendarExceptionsPage.jsx
// =============================================================================
// HR -> Calendar Exceptions
// =============================================================================
//
// PURPOSE
// -------
// This page maintains the facility/company calendar exceptions used by the Leave
// / HR duration engine.
//
// Business background:
// - hb_visits may calculate leave days from exact schedules, rotating patterns,
//   or employee default_daily_minutes fallback.
// - Before any employee-specific rule is applied, the company calendar must be
//   checked. If the salon/facility is closed, that day must not consume the
//   employee's leave balance.
//
// Access model:
// - every authenticated user may view the exception calendar,
// - only superusers may create, edit or deactivate entries.
//
// Architectural notes:
// - this page is internal-shell only; it does not change public layout classes,
// - apiClient/session handling stays inside hrCalendarService,
// - backend remains authoritative for write authorization,
// - frontend role checks are UX only, not security.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import {
  createCalendarException,
  listCalendarExceptions,
  setCalendarExceptionActive,
  updateCalendarException,
} from "../../services/hrCalendarService";

import "./HrMasterDataPage.css";

const TODAY = new Date();
const YEAR = TODAY.getFullYear();

const EMPTY_FORM = {
  id: null,
  exception_date: "",
  type: "NON_WORKING_DAY",
  name: "",
  reason: "",
  minutes_override: "",
  active: true,
};

function toYMD(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function getErrorMessage(err, fallback) {
  const backend =
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";

  if (backend) return String(backend);

  const plain = String(err?.message || "").trim();
  return plain || fallback;
}

function normalizeException(row) {
  return {
    ...EMPTY_FORM,
    ...(row || {}),
    id: row?.id ?? null,
    exception_date: String(row?.exception_date || row?.date || "").slice(0, 10),
    type: String(row?.type || "NON_WORKING_DAY").toUpperCase(),
    name: row?.name || "",
    reason: row?.reason || "",
    minutes_override:
      row?.minutes_override === null || row?.minutes_override === undefined
        ? ""
        : String(row.minutes_override),
    active: row?.active !== false,
  };
}

function typeBadgeClass(type) {
  const normalized = String(type || "").toUpperCase();
  if (normalized === "NON_WORKING_DAY") return "hr-md-badge hr-md-badge--inactive";
  if (normalized === "PARTIAL_WORKING_DAY") return "hr-md-badge";
  return "hr-md-badge";
}

export default function CalendarExceptionsPage() {
  const { t } = useTranslation(["common", "leaves", "navigation"]);
  const { authState } = useContext(AuthContext);

  const isSuperUser = normalizeRole(authState?.user?.role) === "superuser";

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [from, setFrom] = useState(`${YEAR}-01-01`);
  const [to, setTo] = useState(`${YEAR}-12-31`);

  const resetForm = useCallback(() => {
    setForm({ ...EMPTY_FORM, exception_date: toYMD(new Date()) });
  }, []);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listCalendarExceptions({
        from,
        to,
        includeInactive: includeInactive ? "1" : "0",
      });
      setRows(Array.isArray(data) ? data.map(normalizeException) : []);
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:calendarExceptions.loadError")));
    } finally {
      setLoading(false);
    }
  }, [from, includeInactive, t, to]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const haystack = [
        row.exception_date,
        row.type,
        row.name,
        row.reason,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setSuccess("");
    setError("");
  };

  const selectForEdit = (row) => {
    setForm(normalizeException(row));
    setSuccess("");
    setError("");
  };

  const validateForm = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.exception_date)) {
      return t("leaves:calendarExceptions.validation.dateRequired");
    }

    if (!String(form.name || "").trim()) {
      return t("leaves:calendarExceptions.validation.nameRequired");
    }

    if (form.minutes_override !== "") {
      const n = Number(form.minutes_override);
      if (!Number.isInteger(n) || n < 0 || n > 1440) {
        return t("leaves:calendarExceptions.validation.minutesRange");
      }
    }

    return "";
  };

  const buildPayload = () => ({
    exception_date: form.exception_date,
    type: form.type,
    name: String(form.name || "").trim(),
    reason: String(form.reason || "").trim() || null,
    minutes_override: form.minutes_override === "" ? null : Number(form.minutes_override),
    active: Boolean(form.active),
  });

  const handleSave = async (event) => {
    event.preventDefault();

    if (!isSuperUser) return;

    const validation = validateForm();
    if (validation) {
      setError(validation);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (form.id) {
        await updateCalendarException(form.id, buildPayload());
        setSuccess(t("leaves:calendarExceptions.updated"));
      } else {
        await createCalendarException(buildPayload());
        setSuccess(t("leaves:calendarExceptions.created"));
      }

      resetForm();
      await loadRows();
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:calendarExceptions.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    if (!isSuperUser || !row?.id) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const next = !row.active;
      await setCalendarExceptionActive(row.id, next);
      setSuccess(
        next
          ? t("leaves:calendarExceptions.activated")
          : t("leaves:calendarExceptions.deactivated")
      );
      await loadRows();
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:calendarExceptions.toggleError")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hr-md-page">
      <header className="hr-md-header">
        <div>
          <p className="hr-md-eyebrow">{t("navigation:hr")}</p>
          <h1 className="hr-md-title">{t("leaves:calendarExceptions.title")}</h1>
          <p className="hr-md-subtitle">{t("leaves:calendarExceptions.subtitle")}</p>
        </div>
      </header>

      <div className="hr-md-info-box">
        {t("leaves:calendarExceptions.info")}
      </div>

      {error && <div className="hr-md-error">{error}</div>}
      {success && <div className="hr-md-success">{success}</div>}

      <div className="hr-md-grid">
        <section className="hr-md-card">
          <h2 className="hr-md-card-title">
            {form.id
              ? t("leaves:calendarExceptions.editTitle")
              : t("leaves:calendarExceptions.createTitle")}
          </h2>
          <p className="hr-md-card-description">
            {isSuperUser
              ? t("leaves:calendarExceptions.formHelp")
              : t("leaves:calendarExceptions.readOnlyHelp")}
          </p>

          <form onSubmit={handleSave}>
            <div className="hr-md-form-grid">
              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:calendarExceptions.date")}</span>
                <input
                  className="hr-md-input"
                  type="date"
                  value={form.exception_date}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("exception_date", e.target.value)}
                />
              </label>

              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:calendarExceptions.type")}</span>
                <select
                  className="hr-md-select"
                  value={form.type}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("type", e.target.value)}
                >
                  <option value="NON_WORKING_DAY">
                    {t("leaves:calendarExceptions.types.NON_WORKING_DAY")}
                  </option>
                  <option value="SPECIAL_WORKING_DAY">
                    {t("leaves:calendarExceptions.types.SPECIAL_WORKING_DAY")}
                  </option>
                  <option value="PARTIAL_WORKING_DAY">
                    {t("leaves:calendarExceptions.types.PARTIAL_WORKING_DAY")}
                  </option>
                </select>
              </label>

              <label className="hr-md-form-field hr-md-form-field--full">
                <span className="hr-md-label">{t("leaves:calendarExceptions.name")}</span>
                <input
                  className="hr-md-input"
                  value={form.name}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder={t("leaves:calendarExceptions.namePlaceholder")}
                />
              </label>

              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:calendarExceptions.minutesOverride")}</span>
                <input
                  className="hr-md-input"
                  type="number"
                  min="0"
                  max="1440"
                  step="1"
                  value={form.minutes_override}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("minutes_override", e.target.value)}
                />
                <span className="hr-md-hint">
                  {t("leaves:calendarExceptions.minutesOverrideHelp")}
                </span>
              </label>

              <label className="hr-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(form.active)}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("active", e.target.checked)}
                />
                {t("common:status.active")}
              </label>

              <label className="hr-md-form-field hr-md-form-field--full">
                <span className="hr-md-label">{t("leaves:calendarExceptions.reason")}</span>
                <textarea
                  className="hr-md-textarea"
                  value={form.reason}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("reason", e.target.value)}
                  placeholder={t("leaves:calendarExceptions.reasonPlaceholder")}
                />
              </label>
            </div>

            {isSuperUser && (
              <div className="hr-md-actions">
                <button
                  type="button"
                  className="hr-md-button"
                  disabled={saving}
                  onClick={resetForm}
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="hr-md-button hr-md-button--primary"
                  disabled={saving}
                >
                  {saving ? t("common:actions.save") : t("common:actions.save")}
                </button>
              </div>
            )}
          </form>
        </section>

        <section className="hr-md-card">
          <div className="hr-md-toolbar">
            <div className="hr-md-form-grid" style={{ flex: 1 }}>
              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:common.from")}</span>
                <input
                  className="hr-md-input"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:common.to")}</span>
                <input
                  className="hr-md-input"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="hr-md-toolbar">
            <input
              className="hr-md-input hr-md-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("leaves:calendarExceptions.searchPlaceholder")}
            />
            <label className="hr-md-checkbox-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("leaves:calendarExceptions.includeInactive")}
            </label>
          </div>

          <div className="hr-md-table-wrap">
            <table className="hr-md-table">
              <thead>
                <tr>
                  <th>{t("leaves:calendarExceptions.headers.date")}</th>
                  <th>{t("leaves:calendarExceptions.headers.type")}</th>
                  <th>{t("leaves:calendarExceptions.headers.name")}</th>
                  <th>{t("leaves:calendarExceptions.headers.minutes")}</th>
                  <th>{t("leaves:calendarExceptions.headers.status")}</th>
                  <th>{t("leaves:calendarExceptions.headers.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="6">{t("common:common.loading")}</td>
                  </tr>
                )}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="6">{t("leaves:calendarExceptions.empty")}</td>
                  </tr>
                )}

                {!loading &&
                  filteredRows.map((row) => (
                    <tr key={row.id || row.exception_date}>
                      <td>{row.exception_date}</td>
                      <td>
                        <span className={typeBadgeClass(row.type)}>
                          {t(`leaves:calendarExceptions.types.${row.type}`)}
                        </span>
                      </td>
                      <td>
                        <div className="hr-md-row-title">{row.name}</div>
                        {row.reason && (
                          <div className="hr-md-row-subtitle">{row.reason}</div>
                        )}
                      </td>
                      <td>{row.minutes_override === "" ? "—" : row.minutes_override}</td>
                      <td>
                        <span className={row.active ? "hr-md-badge" : "hr-md-badge hr-md-badge--inactive"}>
                          {row.active ? t("common:status.active") : t("common:status.inactive")}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="hr-md-button"
                          onClick={() => selectForEdit(row)}
                        >
                          {isSuperUser ? t("common:actions.edit") : t("common:actions.open")}
                        </button>
                        {isSuperUser && (
                          <button
                            type="button"
                            className="hr-md-button hr-md-button--dangerish"
                            disabled={saving}
                            onClick={() => handleToggleActive(row)}
                          >
                            {row.active ? t("leaves:calendarExceptions.deactivate") : t("leaves:calendarExceptions.activate")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
